const MS_PER_DAY = 86400000

export const DEFAULT_QUOTATION_PREP_DAYS = 7

export const MIN_QUOTATION_WINDOW_DAYS = 5

export type RecommendedQuotationWindow = {
  startDate: string
  endDate: string
}

function formatIsoDateLocal(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function addDaysToIsoDate(iso: string, days: number): string {
  const d = parseIsoDate(iso)
  if (!d || !Number.isFinite(days)) return ''
  const next = new Date(d.getTime())
  next.setDate(next.getDate() + days)
  return formatIsoDateLocal(next)
}

export function computeRecommendedQuotationWindow(
  projectStartDate: string,
  projectEndDate: string,
  prepDays: number = DEFAULT_QUOTATION_PREP_DAYS
): RecommendedQuotationWindow | null {
  const projectStart = String(projectStartDate || '').trim()
  const projectEnd = String(projectEndDate || '').trim()
  if (!projectStart || !projectEnd) return null
  let windowStart = addDaysToIsoDate(projectStart, prepDays)
  if (!windowStart) windowStart = projectStart
  if (windowStart > projectEnd) windowStart = projectStart
  return { startDate: windowStart, endDate: projectEnd }
}

export function normalizeRecommendedQuotationWindow(
  raw: unknown
): RecommendedQuotationWindow | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const o = raw as Record<string, unknown>
  const startDate = String(o.startDate ?? '').trim()
  const endDate = String(o.endDate ?? '').trim()
  if (!startDate || !endDate) return undefined
  return { startDate, endDate }
}

export function quotationWindowMatches(
  current: { startDate?: string; endDate?: string },
  recommended: RecommendedQuotationWindow | undefined
): boolean {
  if (!recommended) return true
  const s = String(current.startDate || '').trim()
  const e = String(current.endDate || '').trim()
  return s === recommended.startDate && e === recommended.endDate
}

export function normalizeIsoDateString(iso: string): string {
  const t = String(iso || '').trim()
  if (!t) return ''
  if (t.includes('T')) return t.split('T')[0] ?? ''
  if (t.includes(' ')) return t.split(' ')[0] ?? ''
  return t
}

export function parseIsoDate(iso: string): Date | null {
  const normalized = normalizeIsoDateString(iso)
  if (!normalized) return null
  const d = new Date(`${normalized}T12:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

export function formatQuotationDisplayDate(iso: string): string {
  const d = parseIsoDate(iso)
  if (!d) return ''
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })
}

const MONTH_NAMES_ES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre'
]

export function formatQuotationDateLong(iso: string): string {
  const t = String(iso || '').trim()
  if (!t) return ''
  const d = new Date(t)
  if (Number.isNaN(d.getTime())) return ''
  const year = d.getUTCFullYear()
  const month = MONTH_NAMES_ES[d.getUTCMonth()]
  const day = d.getUTCDate()
  return `${day} de ${month} ${year}`
}

export function calculateQuotationDaysLeft(endDate: string): number | null {
  const t = String(endDate || '').trim()
  if (!t) return null
  const end = new Date(t)
  if (Number.isNaN(end.getTime())) return null
  const today = new Date()
  const diff = end.getTime() - today.getTime()
  return Math.floor(diff / MS_PER_DAY)
}

export type QuotationCountdownBadge = {
  amountLabel: string
  caption: string
}

export function buildQuotationCountdownBadge(
  windowStartDate: string,
  windowEndDate: string
): QuotationCountdownBadge {
  const start = parseIsoDate(windowStartDate)
  const end = parseIsoDate(windowEndDate)
  if (!start || !end) {
    return { amountLabel: 'No definido', caption: 'Ventana no definida' }
  }

  const todayIso = formatIsoDateLocal(new Date())
  const today = parseIsoDate(todayIso)
  if (!today) {
    return { amountLabel: 'No definido', caption: 'Ventana no definida' }
  }

  const daysToStart = Math.round((start.getTime() - today.getTime()) / MS_PER_DAY)
  const daysToEnd = Math.round((end.getTime() - today.getTime()) / MS_PER_DAY)

  if (daysToEnd < 0) {
    return { amountLabel: String(Math.abs(daysToEnd)), caption: 'días desde cierre' }
  }
  if (daysToStart > 0) {
    return { amountLabel: String(daysToStart), caption: 'días para abrir' }
  }
  return { amountLabel: String(Math.max(daysToEnd, 0)), caption: 'días para cerrar' }
}

export type QuotationTimelineScale = {
  projectStartIso: string
  projectEndIso: string
  maxOffset: number
}

export function buildQuotationTimelineScale(
  projectStartDate: string,
  projectEndDate: string
): QuotationTimelineScale | null {
  const projectStartIso = normalizeIsoDateString(projectStartDate)
  const projectEndIso = normalizeIsoDateString(projectEndDate)
  const ps = parseIsoDate(projectStartIso)
  const pe = parseIsoDate(projectEndIso)
  if (!ps || !pe || pe < ps) return null
  const maxOffset = Math.max(0, inclusiveDaySpan(ps, pe) - 1)
  return { projectStartIso, projectEndIso, maxOffset }
}

export function resolveQuotationTimelineScale(
  projectStartDate: string,
  projectEndDate: string,
  windowStartDate: string,
  windowEndDate: string
): QuotationTimelineScale | null {
  const projectScale = buildQuotationTimelineScale(projectStartDate, projectEndDate)
  if (projectScale) return projectScale

  const windowScale = buildQuotationTimelineScale(windowStartDate, windowEndDate)
  if (windowScale) return windowScale

  const dates = [projectStartDate, projectEndDate, windowStartDate, windowEndDate]
    .map(normalizeIsoDateString)
    .filter(Boolean)
  const parsed = dates.map(parseIsoDate).filter((d): d is Date => d !== null)
  if (parsed.length < 2) return null

  const minTime = Math.min(...parsed.map((d) => d.getTime()))
  const maxTime = Math.max(...parsed.map((d) => d.getTime()))
  const startIso = formatIsoDateLocal(new Date(minTime))
  const endIso = formatIsoDateLocal(new Date(maxTime))
  return buildQuotationTimelineScale(startIso, endIso)
}

export function dayOffsetFromProjectStart(projectStartIso: string, iso: string): number {
  const ps = parseIsoDate(projectStartIso)
  const d = parseIsoDate(iso)
  if (!ps || !d) return 0
  return Math.max(0, Math.round((d.getTime() - ps.getTime()) / MS_PER_DAY))
}

export function isoFromProjectStartOffset(projectStartIso: string, offset: number): string {
  const safe = Math.max(0, Math.floor(offset))
  return addDaysToIsoDate(projectStartIso, safe)
}

export function minQuotationWindowOffsetSpan(scale: QuotationTimelineScale): number {
  const projectDays = scale.maxOffset + 1
  const effectiveMinDays = Math.min(MIN_QUOTATION_WINDOW_DAYS, projectDays)
  return Math.max(0, effectiveMinDays - 1)
}

function clampQuotationWindowOffsetBounds(
  scale: QuotationTimelineScale,
  startOffset: number,
  endOffset: number
): { startOffset: number; endOffset: number } {
  let s = Math.max(0, Math.min(startOffset, scale.maxOffset))
  let e = Math.max(0, Math.min(endOffset, scale.maxOffset))
  if (e < s) e = s
  return { startOffset: s, endOffset: e }
}

function applyMinQuotationWindowOffsetSpan(
  scale: QuotationTimelineScale,
  startOffset: number,
  endOffset: number
): { startOffset: number; endOffset: number } {
  let s = startOffset
  let e = endOffset
  const minSpan = minQuotationWindowOffsetSpan(scale)
  if (minSpan > 0 && e - s < minSpan) {
    const pushEnd = Math.min(scale.maxOffset, s + minSpan)
    if (pushEnd - s >= minSpan) {
      e = pushEnd
    } else {
      s = Math.max(0, e - minSpan)
    }
  }
  return { startOffset: s, endOffset: e }
}

export function clampQuotationWindowOffsets(
  scale: QuotationTimelineScale,
  startOffset: number,
  endOffset: number
): { startOffset: number; endOffset: number } {
  const bounds = clampQuotationWindowOffsetBounds(scale, startOffset, endOffset)
  return applyMinQuotationWindowOffsetSpan(scale, bounds.startOffset, bounds.endOffset)
}

export function enforceMinQuotationWindowDates(
  projectStartDate: string,
  projectEndDate: string,
  startDate: string,
  endDate: string
): { startDate: string; endDate: string } {
  const scale = buildQuotationTimelineScale(projectStartDate, projectEndDate)
  if (!scale || !startDate || !endDate) {
    return { startDate, endDate }
  }
  const offsets = windowOffsetsFromDates(scale, startDate, endDate)
  const clamped = applyMinQuotationWindowOffsetSpan(
    scale,
    offsets.startOffset,
    offsets.endOffset
  )
  return windowDatesFromOffsets(scale, clamped.startOffset, clamped.endOffset)
}

export function windowOffsetsFromDates(
  scale: QuotationTimelineScale,
  windowStartDate: string,
  windowEndDate: string
): { startOffset: number; endOffset: number } {
  const s = dayOffsetFromProjectStart(scale.projectStartIso, windowStartDate)
  const e = dayOffsetFromProjectStart(scale.projectStartIso, windowEndDate)
  return clampQuotationWindowOffsetBounds(scale, s, e)
}

export function windowDatesFromOffsets(
  scale: QuotationTimelineScale,
  startOffset: number,
  endOffset: number
): { startDate: string; endDate: string } {
  const { startOffset: s, endOffset: e } = clampQuotationWindowOffsets(scale, startOffset, endOffset)
  return {
    startDate: isoFromProjectStartOffset(scale.projectStartIso, s),
    endDate: isoFromProjectStartOffset(scale.projectStartIso, e)
  }
}

function inclusiveDaySpan(start: Date, end: Date): number {
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1)
}

export type QuotationTimelineModel = {
  hasTrack: boolean
  prepPercent: number
  windowPercent: number
  showPrep: boolean
  showSameDayWarning: boolean
  showTightPrepWarning: boolean
  prepDays: number
  windowDays: number
  projectStartLabel: string
  windowStartLabel: string
  windowEndLabel: string
  ariaSummary: string
}

export function buildQuotationTimelineModel(input: {
  projectStartDate: string
  projectEndDate: string
  windowStartDate: string
  windowEndDate: string
}): QuotationTimelineModel {
  const projectStart = parseIsoDate(input.projectStartDate)
  const windowStart = parseIsoDate(input.windowStartDate)
  const windowEnd = parseIsoDate(input.windowEndDate)

  const empty: QuotationTimelineModel = {
    hasTrack: false,
    prepPercent: 0,
    windowPercent: 100,
    showPrep: false,
    showSameDayWarning: false,
    showTightPrepWarning: false,
    prepDays: 0,
    windowDays: 0,
    projectStartLabel: formatQuotationDisplayDate(input.projectStartDate),
    windowStartLabel: formatQuotationDisplayDate(input.windowStartDate),
    windowEndLabel: formatQuotationDisplayDate(input.windowEndDate),
    ariaSummary: 'Completá las fechas de la ventana de cotización para ver el calendario.'
  }

  if (!windowStart || !windowEnd || windowEnd < windowStart) {
    return empty
  }

  const windowDays = inclusiveDaySpan(windowStart, windowEnd)
  let prepDays = 0
  let showPrep = false

  if (projectStart && windowStart > projectStart) {
    prepDays = inclusiveDaySpan(projectStart, windowStart) - 1
    if (prepDays < 1) prepDays = 0
    showPrep = prepDays > 0
  }

  const showSameDayWarning = !!projectStart && windowStart.getTime() === projectStart.getTime()
  const showTightPrepWarning = showPrep && prepDays > 0 && prepDays < 7

  let prepPercent = 0
  let windowPercent = 100

  if (showPrep) {
    const rawPrep = prepDays / (prepDays + windowDays)
    prepPercent = Math.round(Math.max(rawPrep * 100, 22))
    windowPercent = 100 - prepPercent
    if (windowPercent < 28) {
      windowPercent = 28
      prepPercent = 72
    }
  }

  const parts: string[] = []
  if (projectStart) {
    parts.push(`Publicación del proyecto desde ${formatQuotationDisplayDate(input.projectStartDate)}.`)
  }
  if (showPrep) {
    parts.push(`${prepDays} días para revisar documentación antes de abrir la ventana.`)
  }
  parts.push(
    `Ventana de envío de presupuestos del ${formatQuotationDisplayDate(input.windowStartDate)} al ${formatQuotationDisplayDate(input.windowEndDate)}.`
  )

  return {
    hasTrack: true,
    prepPercent,
    windowPercent,
    showPrep,
    showSameDayWarning,
    showTightPrepWarning,
    prepDays,
    windowDays,
    projectStartLabel: formatQuotationDisplayDate(input.projectStartDate),
    windowStartLabel: formatQuotationDisplayDate(input.windowStartDate),
    windowEndLabel: formatQuotationDisplayDate(input.windowEndDate),
    ariaSummary: parts.join(' ')
  }
}
